/**
 * DeckGuard — bundle gift linking.
 *
 * The Warden add-to-cart pushes several separate line items in one call:
 *   - the main line, carrying property `_Bundle` = "Buy N"
 *   - the gift lines (ebook / pathies / warranty), carrying `_Bundle gift`
 *
 * They are ordinary line items, not a Shopify bundle, which causes two
 * problems this file fixes:
 *
 *   1. Nothing stopped a shopper deleting the Warden and checking out with
 *      three free gifts. Removing a Warden now removes its gifts in the same
 *      request, and `reconcile()` catches any orphans left by other paths.
 *   2. Shopify orders cart lines by when each was last touched, so a fresh
 *      add-to-cart floats the gifts above their own parent. `group()` puts
 *      them back underneath it.
 */
(() => {
  const cartRoot = () => (window.routes && window.routes.cart_url) || '/cart';

  const isGift = (item) => Boolean(item && item.properties && item.properties['_Bundle gift']);
  const isParent = (item) => Boolean(item && item.properties && item.properties['_Bundle']) && !isGift(item);

  // Sections that may be on screen. Missing ones are simply absent from the
  // response, so it is safe to always ask for all of them.
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];

  const lineKey = (row) => row?.querySelector('[data-quantity-line-key]')?.dataset.quantityLineKey;

  /* ------------------------------------------------------------------
     Visual grouping: parent first, its gifts directly underneath.
     ------------------------------------------------------------------ */

  function group() {
    document.querySelectorAll('.cart-items tbody').forEach((tbody) => {
      const rows = [...tbody.children];
      const isNested = (row) => row.classList.contains('cart-item__nested-line');

      // Each bundle add tags all of its lines with the same `_Bundle` value
      // ("Buy 2"), surfaced on the row as data-dg-bundle. Two different tiers
      // in one cart are therefore two independent groups, and a gift is only
      // ever nested under the parent it actually arrived with.
      const parents = rows.filter((row) => !isNested(row));
      const gifts = rows.filter(isNested);
      if (!gifts.length || !parents.length) return;

      const ordered = [];
      const claimed = new Set();

      parents.forEach((parent) => {
        ordered.push(parent);
        gifts.forEach((gift) => {
          if (claimed.has(gift)) return;
          if (gift.dataset.dgBundle && gift.dataset.dgBundle === parent.dataset.dgBundle) {
            claimed.add(gift);
            ordered.push(gift);
          }
        });
      });

      // Gifts whose parent is gone stay visible until reconcile() clears them,
      // rather than vanishing from the DOM while the cart still bills for them.
      gifts.forEach((gift) => {
        if (!claimed.has(gift)) ordered.push(gift);
      });

      // Already in this order — don't touch the DOM.
      if (ordered.every((row, i) => rows[i] === row)) return;

      ordered.forEach((row) => tbody.appendChild(row));
    });
  }

  /* ------------------------------------------------------------------
     Rendering.
     ------------------------------------------------------------------ */

  const swap = (html, sourceSelector, target) => {
    if (!html || !target) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector(sourceSelector);
    if (source) target.innerHTML = source.innerHTML;
  };

  const repaint = (sections) => {
    if (!sections) return;
    swap(sections['cart-drawer'], '.drawer__inner', document.querySelector('#CartDrawer .drawer__inner'));
    swap(sections['cart-icon-bubble'], '.shopify-section', document.getElementById('cart-icon-bubble'));
    swap(sections['main-cart-items'], '.js-contents', document.querySelector('#main-cart-items .js-contents'));

    const footer = document.getElementById('main-cart-footer');
    swap(sections['main-cart-footer'], '.js-contents', footer && footer.querySelector('.js-contents'));

    const drawer = document.querySelector('cart-drawer');
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (drawer && !bubble) drawer.classList.add('is-empty');

    group();
  };

  async function postUpdates(updates) {
    const url = `${cartRoot()}/update.js?sections=${SECTIONS.join(',')}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!res.ok) throw new Error(`cart update failed: ${res.status}`);

    const cart = await res.json();
    repaint(cart.sections);

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dg-bundle-gifts', cartData: cart });
    }
    return cart;
  }

  /* ------------------------------------------------------------------
     Removing a Warden takes its gifts with it — in one request.

     Letting Dawn delete the parent and then cleaning up afterwards meant two
     round trips, so the gifts lingered on screen for about a second looking
     like free items the shopper had kept. Intercepting the click lets us zero
     the whole group at once, and the rows come off screen immediately while
     the request is still in flight.
     ------------------------------------------------------------------ */

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('cart-remove-button');
      if (!trigger) return;

      const row = trigger.closest('tr.cart-item');
      // Gift lines have no remove button of their own; if one ever appears,
      // let Dawn handle it normally.
      if (!row || row.classList.contains('cart-item__nested-line')) return;

      // Only this Warden's own gifts. Matching on the shared `_Bundle` value
      // rather than "every nested row in the table" — otherwise removing one
      // bundle stripped the gifts off every other bundle in the cart too.
      const bundleId = row.dataset.dgBundle;
      if (!bundleId) return; // not a bundle parent — let Dawn handle it

      const tbody = row.closest('tbody');
      const gifts = tbody
        ? [...tbody.querySelectorAll('tr.cart-item__nested-line')].filter(
            (gift) => gift.dataset.dgBundle === bundleId
          )
        : [];
      if (!gifts.length) return;

      const parentKey = lineKey(row);
      if (!parentKey) return; // fall through to Dawn rather than guess

      const updates = { [parentKey]: 0 };
      gifts.forEach((gift) => {
        const key = lineKey(gift);
        if (key) updates[key] = 0;
      });

      // Capture phase, so this runs before Dawn's own listener on the element.
      event.preventDefault();
      event.stopPropagation();

      [row, ...gifts].forEach((node) => node.remove());

      postUpdates(updates).catch((error) => {
        console.error('[DeckGuard] bundle removal failed; reloading.', error);
        window.location.reload();
      });
    },
    true
  );

  /* ------------------------------------------------------------------
     Safety net: orphaned gifts from any path we did not intercept — the
     /cart page's plain remove links, quantity set to zero, apps, or a
     removal done in another tab.
     ------------------------------------------------------------------ */

  let busy = false;

  async function reconcile() {
    if (busy) return;
    busy = true;

    try {
      const res = await fetch(`${cartRoot()}.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;

      const items = (await res.json()).items || [];
      const bundleOf = (item) => (item.properties && item.properties['_Bundle']) || '';

      // Reconcile per bundle, not across the whole cart: a cart holding a
      // Buy 2 and a Buy 3 has two independent groups, and losing the parent
      // of one must not touch the other's gifts.
      const parents = new Set(items.filter(isParent).map(bundleOf));
      const orphans = items.filter((item) => isGift(item) && !parents.has(bundleOf(item)));

      if (!orphans.length) return;

      const updates = {};
      orphans.forEach((item) => {
        updates[item.key] = 0;
      });

      await postUpdates(updates);
    } catch (error) {
      // A failed cleanup must never break the cart UI — the next cart change
      // (or the next page load) will try again.
      console.warn('[DeckGuard] gift cleanup skipped:', error);
    } finally {
      busy = false;
    }
  }

  const start = () => {
    group();
    reconcile();

    // Dawn replaces the cart markup wholesale on every change, including the
    // node that used to be observed here — which is why grouping only took
    // effect on the second render. Observe the custom element instead: it is
    // never replaced, so the observer survives every rerender. Callbacks run
    // as microtasks, before paint, so the reorder is never visible.
    const observer = new MutationObserver(() => group());
    ['cart-drawer', '#main-cart-items'].forEach((selector) => {
      const node = document.querySelector(selector);
      if (node) observer.observe(node, { childList: true, subtree: true });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  const subscribeWhenReady = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'dg-bundle-gifts') return; // our own echo
      group();
      reconcile();
    });
    return true;
  };

  if (!subscribeWhenReady()) {
    document.addEventListener('DOMContentLoaded', subscribeWhenReady, { once: true });
  }
})();
